import { isMobileHeaderSheetOpenAtom } from '@/app-shell/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import {
  chatDraftFilesAtomFamily,
  chatDraftTextAtomFamily,
  clearLinkedContextAtom,
  evidenceInputActiveAtom,
  NEW_CHAT_DRAFT_KEY,
} from '@/atoms/chatAtoms';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { useBaseChatHref, useResetProjectsComposerContext } from '@heidi/agents/data';
import { Button } from '@heidi/design-system/button';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { SquarePen } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

/**
 * New Chat action button for the mobile panel header.
 * Extracted from EvidencePanelContent for use in MobileSidebar header.
 * Routes to the flag-aware base chat home so it matches the chat sidebar
 * + "Work" nav.
 */
export function EvidencePanelAction() {
  const { t } = useTranslations();
  const router = useRouter();
  const { trackNewChatClicked } = useEvidenceAnalytics();
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);
  const setEvidenceInputActive = useSetAtom(evidenceInputActiveAtom);
  const setDraftText = useSetAtom(chatDraftTextAtomFamily(NEW_CHAT_DRAFT_KEY));
  const setDraftFiles = useSetAtom(chatDraftFilesAtomFamily(NEW_CHAT_DRAFT_KEY));
  const clearLinkedContext = useSetAtom(clearLinkedContextAtom);
  const baseChatHref = useBaseChatHref();
  const resetProjectsComposerContext = useResetProjectsComposerContext();

  const handleNewChatClick = useCallback(() => {
    trackNewChatClicked('header');
    setEvidenceInputActive(false);
    setDraftText('');
    setDraftFiles([]);
    clearLinkedContext();
    resetProjectsComposerContext();
    setIsMobileHeaderSheetOpen(false);
    void router.push(baseChatHref, undefined, { shallow: false });
  }, [
    trackNewChatClicked,
    setEvidenceInputActive,
    setDraftText,
    setDraftFiles,
    clearLinkedContext,
    resetProjectsComposerContext,
    setIsMobileHeaderSheetOpen,
    router,
    baseChatHref,
  ]);

  return (
    <Button variant="secondary" size="sm" onClick={handleNewChatClick}>
      <SquarePen />
      {t('chat.newChat')}
    </Button>
  );
}
